import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
} from '@angular/core';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdPanelSectionScroller } from '../bodygraph/hd-panel-section-scroller';
import { HdVoiceCardComponent } from './hd-voice-card.component';
import { HdVoiceSectionComponent } from './hd-voice-section.component';
import {
  HD_THROAT_VOICES_WHEEL_ASSET,
  HD_THROAT_VOICE_GATES,
  HD_VOICE_PANEL_GROUPS,
  HdThroatVoicesPanelData,
  HdVoiceGroupKey,
} from './hd-voices.metadata';

interface VoiceGroupSectionVm {
  readonly key: HdVoiceGroupKey;
  readonly gates: readonly number[];
  readonly title: string;
  readonly subtitle: string;
  readonly attention: string;
  readonly solution: string;
  /** Heeft deze groep zichtbare kop-content (titel/subtitel/punten)? */
  readonly hasHeader: boolean;
}

interface ThroatVoicesVm {
  readonly title: string;
  readonly note: string;
  readonly jumpHint: string;
  readonly attentionLabel: string;
  readonly solutionLabel: string;
  readonly groups: readonly VoiceGroupSectionVm[];
}

/**
 * Voices-paneel van de keel (Communicatie cheat sheet): bovenaan de
 * keelcentrum-noot, daaronder de elf voice-blokjes naast het wiel-diagram
 * (klikken springt naar de detailsectie), en tot slot de voices per groep:
 * een groepskop met het aandachtspunt en de oplossing op groepsniveau (waar
 * de cheat sheet die heeft) en per poort een volledige
 * {@link HdVoiceSectionComponent}. Geopend via `HdVoicesPanelService`; een
 * `focusGate` in de paneel-data laat het paneel na render automatisch naar de
 * betreffende poort scrollen (ook bij hergebruik van een al open paneel).
 */
@Component({
  selector: 'hd-throat-voices-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdVoiceCardComponent, HdVoiceSectionComponent],
  templateUrl: './hd-throat-voices-panel.component.html',
  styleUrl: './hd-throat-voices-panel.component.scss',
})
export class HdThroatVoicesPanelComponent implements AfterViewChecked {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdThroatVoicesPanelData>
    | null;

  /** Auto-scroll naar de focus-poort, gedeeld met het angsten-paneel. */
  private readonly scroller = new HdPanelSectionScroller(this.host.nativeElement, this.cdr);

  protected readonly wheelAsset = HD_THROAT_VOICES_WHEEL_ASSET;
  protected readonly gates = HD_THROAT_VOICE_GATES;

  protected readonly vm = computed<ThroatVoicesVm>(() => ({
    title: this.text('voicesPanel.title'),
    note: this.text('note'),
    jumpHint: this.text('voicesPanel.jumpHint'),
    attentionLabel: this.text('voicesPanel.attentionLabel'),
    solutionLabel: this.text('voicesPanel.solutionLabel'),
    groups: HD_VOICE_PANEL_GROUPS.map(group => {
      const title = this.text(`groups.${group.key}.title`);
      const subtitle = this.text(`groups.${group.key}.subtitle`);
      const attention = this.text(`groups.${group.key}.attention`);
      const solution = this.text(`groups.${group.key}.solution`);
      return {
        key: group.key,
        gates: group.gates,
        title,
        subtitle,
        attention,
        solution,
        hasHeader: Boolean(title || subtitle || attention || solution),
      };
    }),
  }));

  constructor() {
    // Beide content-talen eager laden: taalwissel + NL-fallback zonder flikker.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');

    // Elke (her)aanlevering van paneel-data met een focus-poort wapent de
    // auto-scroll opnieuw; het scrollen zelf gebeurt in ngAfterViewChecked
    // zodra de sectie gerenderd is en de scope-tekst geladen is.
    effect(() => {
      const focusGate = this.panelData?.().focusGate ?? null;
      if (focusGate != null) {
        this.scroller.arm(
          `[data-voice-gate="${focusGate}"]`,
          () => this.text(`gates.${focusGate}.attention`) !== '',
        );
      }
    });

    this.destroyRef.onDestroy(() => this.scroller.dispose());
  }

  ngAfterViewChecked(): void {
    this.scroller.onViewChecked();
  }

  protected scrollToGate(gate: number): void {
    this.scroller.jump(`[data-voice-gate="${gate}"]`);
  }

  /** Late afbeelding-load kan de layout verschuiven → extra view-check. */
  protected onWheelLoad(): void {
    this.scroller.notifyLayoutShift();
  }

  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('communication', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('communication', key, 'nl');
  }
}
